import React from 'react';
import { twMerge } from 'utilities/src/twMerge';

import { IFieldProps } from './_props';

import IconComponent from '../../../../Icon';
import LabelIn from '../LabelIn';

import useInputFieldTheme from './themes';

/**
 * Field Component
 */

function Field({
  type,
  label,
  labelPosition,
  state,
  theme,
  variant,
  size = 'Medium',
  id,
  value,
  required = false,
  icon,
  stateIcon,
  iconPosition,
  children,
  className,
  multiSelect = false,
  hasClearOption = false,
  clearOptionIcon,
  onClearInput,
  getToggleButtonProps,
}: IFieldProps) {
  const {
    fieldCommonClasses,
    fieldThemeClasses,
    fieldBehaviorDefaultThemeClasses,
    fieldBehaviorFocusThemeClasses,
    fieldBehaviorFilledThemeClasses,
    fieldStateThemeClasses,
    fieldVariantThemeClasses,
    fieldSizeThemeClasses,
    fieldBackgroundThemeClasses,
    fieldIconCommonClasses,
    fieldIconThemeClasses,
    fieldIconStateThemeClasses,
    fieldIconThemeSize,
    fieldThemeStateIcon,
  } = useInputFieldTheme({
    inputType: type,
    inputState: state,
    inputVariant: variant,
    inputTheme: theme,
    inputSize: size,
    labelPosition,
  });

  return (
    <div
      className={twMerge(
        className,
        fieldCommonClasses,
        fieldThemeClasses,
        fieldBehaviorDefaultThemeClasses,
        fieldBehaviorFocusThemeClasses,
        value && fieldBehaviorFilledThemeClasses,
        fieldStateThemeClasses,
        fieldVariantThemeClasses,
        fieldSizeThemeClasses,
        fieldBackgroundThemeClasses,
        {
          'flex-row-reverse': iconPosition === 'right',
        },
      )}
      id={`label-field-${id}`}
    >
      <IconComponent
        Icon={icon}
        size={fieldIconThemeSize}
        options={{
          className: twMerge(fieldIconCommonClasses, fieldIconThemeClasses),
        }}
      />
      {hasClearOption && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            if (onClearInput) onClearInput();
          }}
          aria-label="Clear"
        >
          <IconComponent
            Icon={clearOptionIcon}
            size={fieldIconThemeSize}
            options={{
              className: twMerge(fieldIconCommonClasses, fieldIconThemeClasses),
            }}
          />
        </button>
      )}

      <div className={`flex flex-1 ${multiSelect ? 'flex-row items-center' : 'flex-col'} flex-wrap leading-5`}>
        <LabelIn
          id={id}
          label={label}
          required={required}
          labelPosition={labelPosition}
          type={type}
          variant={variant}
        />

        {children}
      </div>
      {getToggleButtonProps ? (
        <button type="button" {...getToggleButtonProps()}>
          <IconComponent
            iconName={stateIcon || fieldThemeStateIcon}
            size={fieldIconThemeSize}
            className={twMerge(fieldIconCommonClasses, fieldIconStateThemeClasses)}
          />
        </button>
      ) : (
        <IconComponent
          iconName={stateIcon || fieldThemeStateIcon}
          size={fieldIconThemeSize}
          className={twMerge(fieldIconCommonClasses, fieldIconStateThemeClasses)}
        />
      )}
    </div>
  );
}

export default Field;
